import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useActiveFund } from '../../hooks/useActiveFund';
import { subscribeToDealsByFunds } from '../../services/dealService';
import { getFundById } from '../../services/firestoreService';
import { getSLAConfigsForFund, getDealSLAInfo } from '../../services/slaService';
import PortalLayout from '../../components/shared/Layout/PortalLayout';
import DealTable from '../../components/pipeline/DealTable';

export default function PipelinePage() {
  const { user, userProfile, authReady } = useAuth();
  const { activeFundId, activeStreamId } = useActiveFund();
  const currentUserRoles = userProfile?.roles || (userProfile?.role ? [userProfile.role] : []);
  const [searchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [slaMap, setSlaMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeFund, setActiveFund] = useState(null);

  // Load the selected fund (for base_currency + average_ticket_size). Null on
  // All Funds so DealTable falls back to USD / hides single-fund-only features.
  useEffect(() => {
    let cancelled = false;
    if (!activeFundId) { setActiveFund(null); return; }
    getFundById(activeFundId)
      .then(f => { if (!cancelled) setActiveFund(f); })
      .catch(() => { if (!cancelled) setActiveFund(null); });
    return () => { cancelled = true; };
  }, [activeFundId]);

  // Read initial filter state from URL query parameters (set by dashboard KPI cards)
  const initialTab = searchParams.get('tab') || 'all';
  const initialStatus = searchParams.get('status') || 'active';
  const initialSla = searchParams.get('sla') || '';
  const initialStage = searchParams.get('stage') || '';

  useEffect(() => {
    if (!authReady || !user?.uid) return;
    if (!userProfile) return;
    const isAdmin = (userProfile.roles || [userProfile.role]).includes('admin');

    // Non-admin with no active fund — nothing to show
    if (!isAdmin && !activeFundId) { setLoading(false); return; }

    // Admin with no fund selected → empty array + isAdmin=true → all deals
    // Admin/non-admin with fund selected → scope to that fund
    const activeFundIds = activeFundId ? [activeFundId] : [];

    const unsub = subscribeToDealsByFunds(activeFundIds, async (newDeals) => {
      setDeals(newDeals);
      try {
        const fundIds = [...new Set(newDeals.map(d => d.fund_id).filter(Boolean))];
        const slaConfigs = {};
        for (const fid of fundIds) {
          slaConfigs[fid] = await getSLAConfigsForFund(fid);
        }
        console.log('SLA configs loaded:', Object.entries(slaConfigs).map(([fid, cfg]) => ({
          fund: fid,
          stagesConfigured: Object.keys(cfg || {}).length,
        })));
        const map = {};
        newDeals.forEach(deal => {
          map[deal.id] = getDealSLAInfo(deal, slaConfigs[deal.fund_id] || {});
        });
        setSlaMap(map);
      } catch (err) {
        console.error('SLA fetch failed:', err.code, err.message);
        setSlaMap({});
      } finally {
        setLoading(false);
      }
    }, isAdmin);

    return () => unsub();
  // Re-run when auth, user, or active fund/stream changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, user?.uid, userProfile, activeFundId, activeStreamId]);

  return (
    <PortalLayout>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)' }}>Deal Pipeline</h1>
        <p style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
          {deals.filter(d => d.status === 'active').length} active deals in the selected fund.
        </p>
      </div>
      <DealTable
        deals={deals}
        slaMap={slaMap}
        loading={loading}
        currentUserId={user?.uid}
        currentUserRoles={currentUserRoles}
        currentUserDisplayName={userProfile?.full_name || userProfile?.display_name || user?.email || ''}
        initialTab={initialTab}
        initialStatus={initialStatus}
        initialSlaFilter={initialSla}
        initialStageFilter={initialStage}
        activeFundId={activeFundId}
        activeStreamId={activeStreamId}
        activeFund={activeFund}
      />
    </PortalLayout>
  );
}
